import {
    Peer
} from "peerjs";
import * as THREE from 'three';
import {
    visualizeLog
} from "./utils";
import {
    VoiceChat
} from './voice-chat';

export const EVENT_TYPE_PLAYER_POSITION = 0;
export const EVENT_TYPE_NEW_PLAYER = 1;
export const EVENT_TYPE_PLAYER_DISCONNECT = 2;
export const EVENT_TYPE_MESSAGE = 3;

export class EventPayload {
    constructor(type, data) {
        this.type = type;
        this.data = data;
    }
}

export class Session {
    players = {};
    peer;
    voiceChat;
    scene;
    peerId;
    debug = true;
    currentPosition;
    proximityChatInterval;

    constructor(scene, enableVoiceChat = true, debug = false) {
        // links gui to functions
        // document.getElementById('connect').addEventListener("click", () => this.connect());
        // document.getElementById('disconnect').addEventListener("click", () => this.disconnect());
        document.getElementById('copy-invite').addEventListener("click", () => this.copyInviteLink());

        this.scene = scene;
        this.voiceChat = new VoiceChat();
        this.debug = debug;

        if (enableVoiceChat) {
            // requests microphone access
            this.voiceChat.startVoiceChat(this.players)
                .then(() => {
                    visualizeLog('Voice call initialized')
                    this.enableProximityChat();

                    // add audio
                    for (const peerId of Object.keys(this.players)) {
                        this.closeCall(peerId);
                        this.callPlayer(peerId);
                    }

                });
        }

        this.initPeer();

        if (this.debug) {
            this.initDebug();
        }

    }

    enableProximityChat(updateSpeed = 1000) {
        if (this.proximityChatInterval) {
            return;
        }

        this.proximityChatInterval = setInterval(() => {

            const peers = Object.keys(this.players);

            for (const peerId of peers) {
                const peer = this.players[peerId];
                // check if peer is connected
                if (!peer.dataConnection || !peer.dataConnection.open || !peer.mesh_1 || !peer.mediaConnection || !peer.mediaConnection.open) {
                    continue;
                }

                // check if peer is in proximity
                const distance = peer.mesh_1.position.distanceTo(this.currentPosition);
                this.voiceChat.updatePeerVolume(peerId, distance);
            }
        }, updateSpeed);
    }

    disableProximityChat() {
        if (!this.proximityChatInterval) {
            return;
        }
        clearInterval(this.proximityChatInterval);
        this.proximityChatInterval = undefined;
    }

    /**
     * Networking
     */
    initPeer() {
        if (this.peer !== undefined) {
            visualizeLog('Peer already initialized');
            return;
        }

        this.peer = new Peer();

        this.peer.on('open', peerId => {
            this.peerId = peerId;
            // document.getElementById('peer-id').innerText = peerId;

            // const playerColor = this.getPlayerColorDiv(peerId);
            // document.getElementById('peer-data-container').appendChild(playerColor);

            const invitePeerId = this.checkInviteUrl();
            if (invitePeerId) {
                this.connectToPlayer(invitePeerId);
            }

        });

        // when someone connects this peer start listening for data
        this.peer.on('connection', dataConnection => {
            // waits for the connection to be open
            dataConnection.on('open', () => this.onDataConnection(dataConnection.peer, dataConnection));
            dataConnection.on('error', err => this.onDataCloseOrError(dataConnection.peer, err));
            dataConnection.on('close', () => this.onDataCloseOrError(dataConnection.peer));
            dataConnection.on('disconnected', () => this.onDataCloseOrError(dataConnection.peer));
        });

        // when someone is calling
        this.peer.on('call', mediaConnection => {
            visualizeLog(`mediaConnection call from ${mediaConnection.peer}, your stream is active: ${!!this.voiceChat.stream}`);

            // check if voice chat is already enstablished and if not, accept the call
            const oldMediaConnection = this.players[mediaConnection.peer] && this.players[mediaConnection.peer].mediaConnection && this.players[mediaConnection.peer].mediaConnection.open;

            if (oldMediaConnection) {
                visualizeLog(`Player ${mediaConnection.peer} already has media connection`);
                return;
            }

            mediaConnection.answer(this.voiceChat.stream);
            // handles voice chat
            mediaConnection.on('stream', stream => this.onMediaConnection(mediaConnection.peer, mediaConnection, stream));
        });

        // remove inactive players (in case of onClose never triggered)
        setInterval(() => this.removeInactivePlayers(), 7500);
    }

    removeInactivePlayers() {
        const peers = Object.keys(this.players);
        for (const peerId of peers) {
            const peer = this.players[peerId];

            if (
                peer.dataConnection &&
                peer.dataConnection.open &&
                peer.dataConnection.peerConnection &&
                peer.dataConnection.peerConnection.iceConnectionState === 'disconnected'
            ) {
                this.disconnectPlayer(peerId);
            }
        }
    }

    callPlayer(peerId) {
        visualizeLog(`Calling ${peerId}`);
        const mediaConnection = this.peer.call(peerId, this.voiceChat.stream);
        mediaConnection.on('stream', stream => this.onMediaConnection(mediaConnection.peer, mediaConnection, stream));
        mediaConnection.on('error', err => this.onMediaCloseOrError(peerId, err));
    }

    closeCall(peerId) {
        visualizeLog(`Closing call to ${peerId}`);
        const player = this.players[peerId];
        const mediaConnection = player && player.mediaConnection && player.mediaConnection.open;
        if (!mediaConnection) return;
        mediaConnection.close();
        this.players[peerId].mediaConnection = undefined;
    }

    connectToPlayer(peerId) {
        // avoid connecting to myself or to already connected peers
        if (this.players[peerId] || peerId === this.peerId) {
            visualizeLog(`Player ${peerId} already exists`);
            console.warn(`Player ${peerId} already exists`, this.players[peerId]);
            return;
        }

        visualizeLog(`Connecting with ${peerId}`);
        this.players[peerId] = {};
        const dataConnection = this.peer.connect(peerId);
        dataConnection.on('open', () => this.onDataConnection(peerId, dataConnection));
        dataConnection.on('error', err => this.onDataCloseOrError(peerId, err));
        dataConnection.on('close', () => this.onDataCloseOrError(peerId));
        dataConnection.on('disconnected', () => this.onDataCloseOrError(peerId));
        this.closeConnectionFallback(dataConnection, peerId);

        // skip mediaConnetion if voice chat is not initialized
        if (!this.voiceChat.stream) {
            return;
        }

        this.callPlayer(peerId);
    }

    closeConnectionFallback(connection, peerId, audio = false) {
        // delete player from list if it doesn't respond in 10 seconds
        setTimeout(() => {
            if (connection.open) {
                return;
            }
            visualizeLog(`Player ${peerId} didn't respond`);
            this.disconnectPlayer(peerId);
        }, 10000);
    }

    onDataConnection(peerId, dataConnection) {
        if (this.players[peerId] && this.players[peerId].dataConnection) {
            visualizeLog(`Player ${peerId} already has data connection`);
            return;
        }
        visualizeLog(`Connected with ${peerId}`);

        const newMesh1 = this.getPlayerMesh();
        const newMesh2 = this.getPlayerMesh();
        const newMesh3 = this.getPlayerMesh();
        const newMesh4 = this.getPlayerMesh();

        this.scene.add(newMesh1);
        this.scene.add(newMesh2);
        this.scene.add(newMesh3);
        this.scene.add(newMesh4);

        this.addDataConnection(peerId, dataConnection);
        this.addMesh(peerId, newMesh1, 1);
        this.addMesh(peerId, newMesh2, 2);
        this.addMesh(peerId, newMesh3, 3);
        this.addMesh(peerId, newMesh4, 4);

        // notify every peer about new player
        this.propagatePeers();
        dataConnection.on('data', eventPayload => this.handleData(eventPayload, peerId));
    }

    onMediaConnection(peerId, mediaConnection, stream) {
        if (this.players[peerId] && this.players[peerId].mediaConnection) {
            visualizeLog(`Player ${peerId} already has media connection`);
            return;
        }
        visualizeLog(`Voice call established with ${peerId}`);
        this.addMediaConnection(peerId, mediaConnection);
        mediaConnection.on('close', () => this.onMediaCloseOrError(peerId));
        this.voiceChat.playAudio(stream, peerId);
    }

    handleData(eventPayload, peerId) {
        const data = eventPayload.data;

        if (eventPayload && !eventPayload.type === undefined) {
            console.error(`Invalid data`, eventPayload);
            return;
        }

        switch (eventPayload.type) {
            case EVENT_TYPE_PLAYER_POSITION:
                if (!this.players[peerId]) {
                    return;
                }
                // update player position
                this.players[peerId].mesh_1.position.set(data.position.x, data.position.y, data.position.z);
                this.players[peerId].mesh_2.position.set(data.position.x, -data.position.y, data.position.z);
                this.players[peerId].mesh_3.position.set(data.position.x, data.position.y, -data.position.z);
                this.players[peerId].mesh_4.position.set(data.position.x, -data.position.y, -data.position.z);


                this.players[peerId].mesh_1.rotation.y = data.rotation;
                this.players[peerId].mesh_2.rotation.y = data.rotation;
                this.players[peerId].mesh_3.rotation.y = -data.rotation;
                this.players[peerId].mesh_4.rotation.y = -data.rotation;

                break;

            case EVENT_TYPE_NEW_PLAYER:
                visualizeLog(`New player notification: ${data}`);
                // add new player to the scene
                data.forEach(id => this.connectToPlayer(id));
                break;

            case EVENT_TYPE_PLAYER_DISCONNECT:
                visualizeLog(`Player ${data} disconnected`);
                this.disconnectPlayer(data);
                break;

            case EVENT_TYPE_MESSAGE:
                visualizeLog(`Message from ${peerId}: ${data}`);
                console.log(data);
                break;
        }
    }

    propagatePeers() {
        visualizeLog(`Sending new player id to other nodes`);
        const peers = Object.keys(this.players);
        // send new peer to all other peers
        for (const peer of peers) {

            if (!this.players[peer] || !this.players[peer].dataConnection || !this.players[peer].dataConnection.open) {
                return;
            }

            this.players[peer].dataConnection.send(new EventPayload(EVENT_TYPE_NEW_PLAYER, peers));
        }
    }

    sendPositionUpdate(position, rotation) {
        this.currentPosition = position;
        // send my position to everyone
        for (const peer of Object.keys(this.players)) {
            try {

                if (!this.players[peer] || !this.players[peer].dataConnection || !this.players[peer].dataConnection.open) {
                    return;
                }

                const eventPayload = new EventPayload(
                    EVENT_TYPE_PLAYER_POSITION, {
                        position: {
                            x: position.x,
                            y: position.y,
                            z: position.z,

                        },
                        rotation: rotation
                    }
                );

                this.players[peer].dataConnection.send(eventPayload);

            } catch (error) {
                console.error(error);
            }
        }
    }

    disconnectPlayer(peerId) {
        // check if player exists
        if (!this.players[peerId]) {
            visualizeLog(`Player ${peerId} not found`);
            return;
        }

        visualizeLog(`Disconnecting with ${peerId}`);

        this.voiceChat.stopAudio(peerId);

        if (this.players[peerId] && this.players[peerId].mesh_1) {
            this.scene.remove(this.players[peerId].mesh_1);
            this.scene.remove(this.players[peerId].mesh_2);
            this.scene.remove(this.players[peerId].mesh_3);
            this.scene.remove(this.players[peerId].mesh_4);
        }

        if (this.players[peerId] && this.players[peerId].dataConnection) {
            this.players[peerId].dataConnection.close();
        }

        if (this.players[peerId] && this.players[peerId].mediaConnection) {
            this.players[peerId].mediaConnection.close();
        }

        this.players[peerId] = undefined;
        delete this.players[peerId];
    }

    /**
     * Helpers
     */
    onDataCloseOrError(peerId, err = null) {
        if (err) {
            console.error(err);
        } else {
            visualizeLog(`Player ${peerId} data closed`);
        }
        this.disconnectPlayer(peerId);
    }

    onMediaCloseOrError(peerId, err = null) {
        if (err) {
            console.error(err);
        } else {
            visualizeLog(`Player ${peerId} media closed`);
        }

        if (this.players[peerId] && this.players[peerId].mediaConnection) {
            if (this.players[peerId].mediaConnection.open) {
                this.players[peerId].mediaConnection.close();
            }
            this.players[peerId].mediaConnection = undefined;
        }
    }

    addMediaConnection(peerId, mediaConnection) {
        this.safeAddProperty(peerId, 'mediaConnection', mediaConnection);
    }

    addDataConnection(peerId, dataConnection) {
        this.safeAddProperty(peerId, 'dataConnection', dataConnection);
    }

    addMesh(peerId, mesh, number) {
        this.safeAddProperty(peerId, `mesh_${number}`, mesh);
    }

    safeAddProperty(peerId, property, value) {
        if (!this.players[peerId]) {
            this.players[peerId] = {};
        }
        this.players[peerId][property] = value;
    }

    getPlayerMesh() {

        visualizeLog(`Generating player mesh`);
        const mesh = new THREE.Mesh(
            new THREE.BoxGeometry(2, 20, 2).toNonIndexed(),
            new THREE.MeshBasicMaterial({
                color: 0xffffff,
            }),
        );

        mesh.position.set(0, 10, 0);
        return mesh;
    }

    calcDistanceBetweenVectors(vector1, vector2) {
        return Math.sqrt(Math.pow(vector1.x - vector2.x, 2) + Math.pow(vector1.y - vector2.y, 2) + Math.pow(vector1.z - vector2.z, 2));
    }

    checkInviteUrl() {
        // check url for invites
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        if (urlParams.has('room')) {
            const roomId = urlParams.get('room');
            return roomId;
        }

        return null;
    }

    /**
     * Button handlers
     */
    connect() {
        const id = document.getElementById('id').value;
        this.connectToPlayer(id);
    }

    disconnect() {
        for (const peer of Object.keys(this.players)) {
            this.disconnectPlayer(peer);
        }

        this.players = {};
    }

    copyInviteLink() {
        if (!this.peerId) {
            return;
        }



        const url = `${window.location.origin}${window.location.pathname}?room=${this.peerId}`;
        navigator.clipboard.writeText(url)

        try {
            navigator.share({
                title: 'TOGHETER',
                text: url
            });
        } catch (error) {}
    }

    /**
     * Debug
     */
    initDebug() {
        setInterval(() => this.debugDrawConnectedPlayers(), 1000);
    }

    getSimplifiedCoordinates(position) {
        if (!position) {
            return null;
        }

        return {
            x: position.x.toFixed(2),
            y: position.y.toFixed(2),
            z: position.z.toFixed(2),
        };
    }

    getPlayerColorDiv(peerId) {
        const div = document.createElement('div');
        div.style = `background-color: #${peerId.substring(peerId.length - 6)}; height: 20px; width: 20px;`;
        return div;
    }

    debugDrawConnectedPlayers() {
        const playersContainer = document.getElementById('room-players');
        playersContainer.innerHTML = '';

        for (const peerId of Object.keys(this.players)) {

            const player = this.players[peerId];

            if (!player.mesh_1) {
                return
            }

            const div = document.createElement('div');
            const playerContainer = document.createElement('pre');

            const playerColor = this.getPlayerColorDiv(peerId)
            div.appendChild(playerColor);

            playerContainer.style = 'font-size: 10px; color: white; margin: 0;margin-right: 6px;margin-bottom: 6px;';
            const playerPosition = this.getSimplifiedCoordinates(player.mesh_1.position);

            playerContainer.innerHTML = `${ playerPosition?.x }, ${ playerPosition?.y }, ${ playerPosition?.z } \nConnected: ${!!player?.dataConnection?.open}\nVoice: ${!!player?.mediaConnection?.open}`;
            div.appendChild(playerContainer);
            playersContainer.appendChild(div);
        }
    }

}